/* global Office, localStorage */

// Durable key/value storage for the add-in.
//
// Uses Office.context.roamingSettings when available — it's persisted on the
// Exchange server, so it survives the task-pane reload OWA forces when the user
// pins the pane or switches messages (which recreates the add-in iframe and
// wipes sessionStorage). Falls back to localStorage outside Office.
//
// This is the same mechanism the auth layer relies on, which is why the signed-in
// session survives those reloads.

export function hasRoamingSettings(): boolean {
  return (
    typeof Office !== "undefined" &&
    !!Office.context &&
    !!Office.context.roamingSettings &&
    typeof Office.context.roamingSettings.saveAsync === "function"
  );
}

// Synchronous read. roamingSettings.get returns the stored object directly;
// localStorage values are JSON-parsed.
export function getSetting<T>(key: string): T | null {
  if (hasRoamingSettings()) {
    const raw = Office.context.roamingSettings.get(key) as T | undefined;
    return raw ?? null;
  }
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  if (hasRoamingSettings()) {
    Office.context.roamingSettings.set(key, value);
    await new Promise<void>((resolve) => {
      Office.context.roamingSettings.saveAsync(() => resolve());
    });
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode / blocked iframe) — skip */
  }
}

export async function removeSetting(key: string): Promise<void> {
  if (hasRoamingSettings()) {
    Office.context.roamingSettings.remove(key);
    await new Promise<void>((resolve) => {
      Office.context.roamingSettings.saveAsync(() => resolve());
    });
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}
